import {
  importCsvFilesLocalFirst,
  importExcelFilesLocalFirst,
} from '@affine/core/modules/streamlain/file-import';
import { classifyEdgelessImportFile } from '@affine/core/modules/streamlain/file-import/edgeless-import-classifier';
import { FullscreenRegistry } from '@affine/core/modules/streamlain/services/fullscreen-registry';
import { WorkbenchService } from '@affine/core/modules/workbench';
import {
  resolveBlobDisplayName,
  resolveBlobDownloadName,
} from '@affine/core/utils/blob-display-name';
import { I18n } from '@affine/i18n';
import track from '@affine/track';
import { toast } from '@blocksuite/affine/components/toast';
import { AttachmentBlockModel } from '@blocksuite/affine/model';
import {
  EMBED_CARD_HEIGHT,
  EMBED_CARD_WIDTH,
} from '@blocksuite/affine/shared/consts';
import {
  ActionPlacement,
  type ToolbarAction,
  type ToolbarContext,
  type ToolbarModuleConfig,
} from '@blocksuite/affine/shared/services';
import { DocxTransformer } from '@blocksuite/affine/widgets/linked-doc';
import { Bound, type SerializedXYWH } from '@blocksuite/global/gfx';
import {
  DatabaseTableViewIcon,
  ExpandFullIcon,
  LinkedPageIcon,
  SplitViewIcon,
} from '@blocksuite/icons/lit';
import type { BlockModel } from '@blocksuite/store';
import type { FrameworkProvider } from '@toeverything/infra';

const LINKED_DOC_CARD_STYLE = 'horizontalThin' as const;

function attachmentLabel(key: string) {
  return I18n.t(`com.streamlain.attachment.toolbar.${key}`);
}

function getAttachmentModel(ctx: ToolbarContext) {
  return ctx.getCurrentModelByType(AttachmentBlockModel);
}

function getAttachmentClassification(model: AttachmentBlockModel) {
  return classifyEdgelessImportFile({
    name: resolveBlobDisplayName({
      blobId: model.props.sourceId,
      occurrenceName: model.props.name,
      mimeType: model.props.type,
    }),
    type: model.props.type,
  });
}

function isDatabaseConvertibleAttachment(model: AttachmentBlockModel) {
  const classification = getAttachmentClassification(model);
  if (!classification) return false;
  if (classification.status === 'unsupported') {
    return classification.reason === 'legacy-xls';
  }
  return (
    classification.fileType === 'csv' || classification.fileType === 'excel'
  );
}

function isDocxAttachment(model: AttachmentBlockModel) {
  const classification = getAttachmentClassification(model);
  return (
    classification?.status === 'supported' && classification.fileType === 'docx'
  );
}

function isPdfAttachment(model: AttachmentBlockModel) {
  return (
    model.props.type.toLowerCase() === 'application/pdf' ||
    resolveBlobDisplayName({
      blobId: model.props.sourceId,
      occurrenceName: model.props.name,
      mimeType: model.props.type,
    })
      .toLowerCase()
      .endsWith('.pdf')
  );
}

function fileTitle(fileName: string) {
  return (
    fileName.replace(/\.[^.]+$/u, '').trim() || fileName || 'Imported file'
  );
}

async function readAttachmentFile(model: AttachmentBlockModel): Promise<File> {
  const sourceId = model.props.sourceId;
  if (!sourceId) {
    throw new Error(attachmentLabel('missingBlob'));
  }

  const blob = await model.store.blobSync.get(sourceId);
  if (!blob) {
    throw new Error(attachmentLabel('missingBlob'));
  }

  return new File(
    [blob],
    resolveBlobDownloadName({
      blobId: sourceId,
      occurrenceName: model.props.name,
      mimeType: model.props.type,
      fallbackName: 'attachment',
    }),
    {
      type: model.props.type || 'application/octet-stream',
    }
  );
}

function getPersistedBound(model: AttachmentBlockModel | BlockModel) {
  const xywh = (model.props as { xywh?: unknown }).xywh;
  if (typeof xywh !== 'string' || xywh.length === 0) return null;

  try {
    return Bound.deserialize(xywh as SerializedXYWH);
  } catch {
    return null;
  }
}

function replaceFirstImportedDatabaseBound(
  ctx: ToolbarContext,
  databaseBlockIds: readonly string[],
  sourceBound: Bound | null
) {
  if (!sourceBound || !databaseBlockIds[0]) return;

  const database = ctx.store.getBlock(databaseBlockIds[0])?.model;
  if (!database) return;

  const currentBound = getPersistedBound(database);
  const width =
    currentBound?.w && currentBound.w > 0 ? currentBound.w : sourceBound.w;
  const height =
    currentBound?.h && currentBound.h > 0 ? currentBound.h : sourceBound.h;
  ctx.store.updateBlock(database, {
    xywh: new Bound(sourceBound.x, sourceBound.y, width, height).serialize(),
  });
}

function selectSurfaceElements(ctx: ToolbarContext, elementIds: string[]) {
  if (!ctx.isEdgelessMode || elementIds.length === 0) return;
  ctx.gfx.selection.set({
    elements: elementIds,
    editing: false,
  });
  ctx.reset();
}

async function convertAttachmentToDatabase(ctx: ToolbarContext) {
  const model = getAttachmentModel(ctx);
  if (!model) return;

  const classification = getAttachmentClassification(model);
  if (classification?.status === 'unsupported') {
    toast(ctx.host, attachmentLabel('legacyXlsUnsupported'));
    return;
  }
  if (
    classification?.status !== 'supported' ||
    (classification.fileType !== 'csv' && classification.fileType !== 'excel')
  ) {
    return;
  }

  const file = await readAttachmentFile(model);
  const sourceBound = getPersistedBound(model);
  const dropPosition = sourceBound
    ? { x: sourceBound.x, y: sourceBound.y }
    : undefined;
  const options = {
    collection: ctx.workspace,
    docId: ctx.store.id,
    docName: fileTitle(file.name),
    dropPosition,
    extensions: ctx.store.userExtensions,
    files: [file],
  };
  const result =
    classification.fileType === 'csv'
      ? await importCsvFilesLocalFirst(options)
      : await importExcelFilesLocalFirst(options);

  replaceFirstImportedDatabaseBound(ctx, result.databaseBlockIds, sourceBound);
  ctx.store.deleteBlock(model.id);
  selectSurfaceElements(ctx, result.databaseBlockIds);
  toast(ctx.host, attachmentLabel('databaseImported'));
}

function addLinkedDocCard(
  ctx: ToolbarContext,
  model: AttachmentBlockModel,
  pageId: string
) {
  if (!ctx.store.schema.flavourSchemaMap.has('affine:embed-linked-doc')) {
    return null;
  }

  const parentId = model.parent?.id;
  if (!parentId) return null;

  const sourceBound = getPersistedBound(model);
  const xywh = sourceBound
    ? new Bound(
        sourceBound.x,
        sourceBound.y,
        EMBED_CARD_WIDTH[LINKED_DOC_CARD_STYLE],
        EMBED_CARD_HEIGHT[LINKED_DOC_CARD_STYLE]
      ).serialize()
    : undefined;

  return ctx.store.addBlock(
    'affine:embed-linked-doc' as never,
    {
      pageId,
      style: LINKED_DOC_CARD_STYLE,
      ...(xywh ? { xywh } : {}),
    },
    parentId
  );
}

async function convertAttachmentToDocument(ctx: ToolbarContext) {
  const model = getAttachmentModel(ctx);
  if (!model || !isDocxAttachment(model)) return;

  const file = await readAttachmentFile(model);
  const docId = await DocxTransformer.importDocx({
    collection: ctx.workspace,
    schema: ctx.store.schema,
    imported: file,
    extensions: ctx.store.userExtensions,
    fileName: fileTitle(file.name),
  });
  if (!docId) {
    throw new Error(attachmentLabel('documentImportFailed'));
  }

  const linkedDocBlockId = addLinkedDocCard(ctx, model, docId);
  ctx.store.deleteBlock(model.id);
  if (linkedDocBlockId) {
    selectSurfaceElements(ctx, [linkedDocBlockId]);
  } else {
    ctx.reset();
  }
  toast(ctx.host, attachmentLabel('documentImported'));
}

function runAttachmentConversion(
  ctx: ToolbarContext,
  action: (ctx: ToolbarContext) => Promise<void>
) {
  ctx.hide();
  void action(ctx).catch(error => {
    console.error('[attachment-conversion] failed', error);
    toast(
      ctx.host,
      error instanceof Error
        ? error.message
        : attachmentLabel('conversionFailed')
    );
    ctx.show();
  });
}

const importAsDatabaseAction = {
  placement: ActionPlacement.More,
  id: 'b.streamlain-import-as-database',
  get label() {
    return attachmentLabel('importAsDatabase');
  },
  icon: DatabaseTableViewIcon(),
  when(ctx) {
    const model = getAttachmentModel(ctx);
    return Boolean(
      model?.props.sourceId && isDatabaseConvertibleAttachment(model)
    );
  },
  run(ctx) {
    runAttachmentConversion(ctx, convertAttachmentToDatabase);
  },
} as const satisfies ToolbarAction;

const importAsDocumentAction = {
  placement: ActionPlacement.More,
  id: 'c.streamlain-import-as-document',
  get label() {
    return attachmentLabel('importAsDocument');
  },
  icon: LinkedPageIcon(),
  when(ctx) {
    const model = getAttachmentModel(ctx);
    return Boolean(model?.props.sourceId && isDocxAttachment(model));
  },
  run(ctx) {
    runAttachmentConversion(ctx, convertAttachmentToDocument);
  },
} as const satisfies ToolbarAction;

export function createAttachmentConversionToolbarConfig(
  framework: FrameworkProvider
): ToolbarModuleConfig {
  const openPdfFullscreenAction = {
    placement: ActionPlacement.More,
    id: 'a.streamlain-open-pdf-fullscreen',
    get label() {
      return I18n.t('com.streamlain.workbench.split-view-menu.full-screen');
    },
    icon: ExpandFullIcon(),
    when(ctx) {
      const model = getAttachmentModel(ctx);
      return Boolean(model && isPdfAttachment(model));
    },
    run(ctx) {
      const model = getAttachmentModel(ctx);
      if (!model) return;

      framework.get(FullscreenRegistry).open('attachment', model.id);
      track.$.attachment.$.openAttachmentInFullscreen({
        type: model.props.type,
      });
      ctx.hide();
    },
  } as const satisfies ToolbarAction;

  const openPdfInSplitViewAction = {
    placement: ActionPlacement.More,
    id: 'a.streamlain-open-pdf-in-split-view',
    get label() {
      return I18n.t(
        'com.streamlain.peek-view-controls.open-attachment-in-split-view'
      );
    },
    icon: SplitViewIcon(),
    when(ctx) {
      const model = getAttachmentModel(ctx);
      return Boolean(model && isPdfAttachment(model));
    },
    run(ctx) {
      const model = getAttachmentModel(ctx);
      if (!model) return;

      framework
        .get(WorkbenchService)
        .workbench.openAttachment(model.store.id, model.id, { at: 'beside' });
      track.$.attachment.$.openAttachmentInSplitView({
        type: model.props.type,
      });
      ctx.hide();
    },
  } as const satisfies ToolbarAction;

  return {
    actions: [
      openPdfFullscreenAction,
      openPdfInSplitViewAction,
      importAsDatabaseAction,
      importAsDocumentAction,
    ],
  };
}
